/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import { default as isBefore } from 'date-fns/isBefore';
import { default as parseISO } from 'date-fns/parseISO';
import { camelCase } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';

import { getImageAtSize } from '../../../lib/utils';

import { useThemeClassName, useTheme } from '../../../contexts/ThemeContext';
import Button from '../../buttons/Button';
import ExplanationModal from '../../modals/ExplanationModal';

import styles from '../../../../styles/items/games/default.module.scss';

import lockIcon from '../../../../img/voisin/locked.svg';

interface DefaultGameItemProps {
    game: Game;
    participation?: Participation | null;
    canParticipate?: boolean;
    canParticipateAt?: string | null;
    className?: string | null;
    cardClassName?: string | null;
}

function DefaultGameItem({
    game,
    participation = null,
    canParticipate = true,
    canParticipateAt = null,
    className = null,
    cardClassName = null,
}: DefaultGameItemProps) {
    const { name: theme } = useTheme();
    const themeClassName = useThemeClassName(styles);
    const {
        type,
        url,
        title = null,
        image = null,
        startAt = null,
        endAt = null,
        subtitle = null,
        explanationModal = null,
        // canParticipate: gameCanParticipate = true,
    } = game || {};

    const itemClassName = subtitle !== null ? camelCase(subtitle) : null;

    const [modalOpen, setModalOpen] = useState(false);
    const openModal = useCallback(() => setModalOpen(true), [setModalOpen]);
    const closeModal = useCallback(() => {
        setModalOpen(false);
    }, [setModalOpen]);

    const startAtDate = useMemo(() => (startAt !== null ? parseISO(startAt) : null), [startAt]);
    const endAtDate = useMemo(() => (endAt !== null ? parseISO(endAt) : null), [endAt]);
    const canParticipateAtDate = useMemo(
        () => (canParticipateAt !== null ? parseISO(canParticipateAt) : null),
        [canParticipateAt],
    );

    const isStarted = useMemo(
        () => startAtDate === null || isBefore(startAtDate, new Date()),
        [startAtDate],
    );

    const isEnded = useMemo(
        () => endAtDate !== null && isBefore(endAtDate, new Date()),
        [endAtDate],
    );

    const inactive = useMemo(
        () => !isStarted || isEnded || !canParticipate,
        [isStarted, isEnded, canParticipate],
    );

    const {
        url: imageUrl = null,
        width: imageWidth = null,
        height: imageHeight = null,
    } = getImageAtSize(image, 'medium') || {};

    // const finalCanParticipate = canParticipate;
    // console.log('title', title);

    return (
        <div
            className={classNames([
                styles.container,
                styles[type],
                {
                    [styles.inactive]: inactive,
                    [itemClassName]: itemClassName !== null,
                },
                themeClassName,
                className,
                'default-game-item',
            ])}
        >
            <div className={styles.inner}>
                <Button
                    href={
                        isStarted &&
                        !isEnded &&
                        (explanationModal === null || participation !== null)
                            ? url
                            : null
                    }
                    onClick={
                        !modalOpen && explanationModal !== null && participation === null
                            ? openModal
                            : null
                    }
                    disabled={!isStarted || isEnded}
                    className={classNames([
                        styles.card,
                        {
                            // [styles.disabled]: !finalCanParticipate,
                            [cardClassName]: cardClassName !== null,
                        },
                    ])}
                >
                    {title !== null && imageUrl === null ? (
                        <h3 className={styles.title}>{title}</h3>
                    ) : null}
                    {theme === 'voisin' && inactive ? (
                        <img className={styles.lockIcon} src={lockIcon} alt="Lock" />
                    ) : null}
                    {imageUrl !== null ? (
                        <div
                            className={styles.imageContainer}
                            style={{
                                paddingBottom:
                                    theme === 'iga_noel'
                                        ? `${(imageHeight / imageWidth) * 100}%`
                                        : null,
                            }}
                        >
                            <img src={imageUrl} alt={title} className={styles.image} />
                        </div>
                    ) : null}
                    <span className={styles.button}>
                        {isEnded ? (
                            <FormattedMessage
                                defaultMessage="Ended on {date}"
                                values={{
                                    date: (
                                        <FormattedDate
                                            value={endAtDate}
                                            day="numeric"
                                            month="short"
                                        />
                                    ),
                                }}
                                description="Button label"
                            />
                        ) : null}

                        {!isStarted ? (
                            <FormattedMessage
                                defaultMessage="Start on {date}"
                                values={{
                                    date: (
                                        <FormattedDate
                                            value={startAtDate}
                                            day="numeric"
                                            month="short"
                                        />
                                    ),
                                }}
                                description="Button label"
                            />
                        ) : null}

                        {isStarted && !isEnded && canParticipateAtDate === null ? (
                            <FormattedMessage defaultMessage="Play" description="Button label" />
                        ) : null}

                        {isStarted && !isEnded && canParticipateAtDate !== null ? (
                            <FormattedMessage
                                defaultMessage="Play on {date}"
                                values={{
                                    date: (
                                        <FormattedDate
                                            value={canParticipateAtDate}
                                            day="numeric"
                                            month="short"
                                        />
                                    ),
                                }}
                                description="Button label"
                            />
                        ) : null}
                    </span>
                </Button>
            </div>
            {modalOpen ? (
                <ExplanationModal
                    blocks={explanationModal}
                    url={isStarted && !isEnded ? url : null}
                    onClose={closeModal}
                />
            ) : null}
        </div>
    );
}

export default DefaultGameItem;
